import {
  AiApplicationInterfaceMetadataSchema,
  type AiApplicationDto,
} from '@terra/shared-contracts-ai-applications';

export type AiApplicationInterfaceMetadata =
  AiApplicationDto['interfaces'][number]['metadata'];

function readParsedInterfaceMetadata(
  metadata: AiApplicationInterfaceMetadata,
): NonNullable<AiApplicationInterfaceMetadata> | null {
  const parsed = AiApplicationInterfaceMetadataSchema.safeParse(metadata);
  if (!parsed.success || parsed.data == null) {
    return null;
  }

  return parsed.data;
}

function readInterfaceMetadataBase(
  metadata: AiApplicationInterfaceMetadata,
): NonNullable<AiApplicationInterfaceMetadata> {
  const parsed = readParsedInterfaceMetadata(metadata);
  if (parsed) {
    return parsed;
  }

  if (
    metadata != null &&
    typeof metadata === 'object' &&
    !Array.isArray(metadata)
  ) {
    return { ...metadata };
  }

  return {};
}

export function readInterfaceBodyExample(
  metadata: AiApplicationInterfaceMetadata,
): string | null {
  const parsedMetadata = readParsedInterfaceMetadata(metadata);
  if (!parsedMetadata) {
    return null;
  }

  const bodyExample = parsedMetadata.bodyExample;
  return typeof bodyExample === 'string' && bodyExample.trim().length > 0
    ? bodyExample
    : null;
}

export function readInterfaceDiscoveryFailureReason(
  metadata: AiApplicationInterfaceMetadata,
): string | null {
  const parsedMetadata = readParsedInterfaceMetadata(metadata);
  if (!parsedMetadata) {
    return null;
  }

  const failureReason = parsedMetadata.discoveryFailureReason;
  return typeof failureReason === 'string' && failureReason.trim().length > 0
    ? failureReason.trim()
    : null;
}

export function withInterfaceBodyExample({
  metadata,
  bodyExample,
}: {
  metadata: AiApplicationInterfaceMetadata;
  bodyExample: string | null | undefined;
}): NonNullable<AiApplicationInterfaceMetadata> {
  const base = readInterfaceMetadataBase(metadata);
  const trimmed = bodyExample?.trim();

  if (trimmed) {
    return { ...base, bodyExample: trimmed };
  }

  const { bodyExample: _removed, ...rest } = base;
  return rest;
}

export type InterfaceProbeStatus =
  | 'PENDING'
  | 'VERIFIED'
  | 'FAILED'
  | 'SKIPPED';

export function isInterfaceDiscoveryPending(
  metadata: AiApplicationInterfaceMetadata,
): boolean {
  const parsedMetadata = readParsedInterfaceMetadata(metadata);
  if (!parsedMetadata) {
    return false;
  }

  return parsedMetadata.discoveryPending === true;
}

export function getInterfaceProbeStatus(
  metadata: AiApplicationInterfaceMetadata,
): InterfaceProbeStatus | null {
  const parsedMetadata = readParsedInterfaceMetadata(metadata);
  if (!parsedMetadata) {
    return null;
  }

  if (parsedMetadata.probeStatus) {
    return parsedMetadata.probeStatus;
  }

  return parsedMetadata.discoveryPending ? 'PENDING' : null;
}

export function getProbeStatusPalette(
  probeStatus: InterfaceProbeStatus | null,
) {
  switch (probeStatus) {
    case 'VERIFIED':
      return 'green';
    case 'FAILED':
      return 'red';
    case 'SKIPPED':
      return 'gray';
    case 'PENDING':
      return 'yellow';
    default:
      return 'gray';
  }
}
